import { useState, useEffect, useRef, useCallback, useContext } from "react";
import "../Candidates.css";
import { get, post } from "../../../../helper/Api_helper";
import { STREAMING_ENABLED } from "../../../../helper/featureFlags";
import { useThinkingFallback } from "../../../../helper/useThinkingFallback";
import { RECOVERY_FAILED_MESSAGE } from "../../../../helper/chatRecovery";
import {
  VYLABS_BASE_URL,
  CREATE_CHAT_CHANNEL_URL,
  PROCESS_JOB_CONVERSATION,
  WEB_SOCKET_URL,
  GET_JOB_DETAILS_BY_ID,
  GET_CURRENT_CONVERSATION_TITLE,
} from "../../../../helper/url_helper";
import JobsCard, {
  JobStatusUpdatePopup,
} from "../../../../Common/Card/JobsCard";
import { Toast } from "../../../../Common/Toast-Snackbar/ToastSnackbar";
import Badge from "@mui/material/Badge";
import javaIcon from "../../../../Assets/Images/javaIcon.png";
import excelIcon from "../../../../Assets/Images/excelIcon.png";
import phpIcon from "../../../../Assets/Images/phpIcon.png";
import reactIcon from "../../../../Assets/Images/reactIcon.png";
import pythonIcon from "../../../../Assets/Images/pythonIcon.png";
import androidIcon from "../../../../Assets/Images/androidIcon.png";
import AnimationPlayer from "../../../../Common/Animation-Player/AnimationPlayer";
import BotReplyIcon from "../../../../Assets/Images/VybogTAL GEN Icon White.svg";
import ThinkingBubble from "../../../../Common/ThinkingBubble/ThinkingBubble";
import CustomTooltip from "../../../../Common/Tooltip/Tooltip";
import VybogTALWhiteIcon from "../../../../Assets/Images/VybogTAL GEN Icon White.svg";
import networkIcon from "../../../../Assets/Images/Network-badge-icon.svg";
import JobsDetailsContainer from "../../../../Common/Jobs-Details-Container/JobsDetailsContainer";
import { v4 as uuidv4 } from "uuid";
import { ConfirmationPopup } from "../../../../Common/PopupStyles/PopupComponent";
import NojobsAnimation from "../../../../Assets/Json/nodata-found-robot-animation.json";
import { DynamicContext } from "../../../../Context/DynamicContext";
import VybogTALLoaderAnimation from "../../../../Common/Dynamic-Animations/DynamicAnimations";
import MatchPercentage from "../../../../Common/Match-percentage-popup/MatchPercentage";
import {
  sanitizeBotText,
  parseMessageText,
  BotResponseStarIcon,
  renderInlineMarkdown,
  getBulletContent,
  renderBotContent,
} from "../../../../Common/Common-Function/CommonFunctions";
import Button from "../../../../Common/Button/Button";
import RecommendedSearchBox from "../../../../Common/RecommendedSearchBox/RecommendedSearchBox";

export const jobsCache = new Map();
export const channelIdCache = new Map();

function RecommendedJobs({
  candidate,
  CandidatehasSearched,
  hideSearchBox,
  isRecommendedJobsLoading = false,
}) {
  const { setIsFromCandidateRecommendedJobs, isDarkMode } =
    useContext(DynamicContext);
  const candidateId = candidate?.id;
  const [recommendedMessages, setRecommendedMessages] = useState([]);
  const [searchMessages, setSearchMessages] = useState([]);
  const [isBotProcessing, setIsBotProcessing] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [jobsSideContainerOpen, setJobsSideContainerOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [activeTab, setActiveTab] = useState("Job Details");
  const [assignedCandidates, setAssignedCandidates] = useState([]);
  const [isFirstBotResponse, setIsFirstBotResponse] = useState(true);
  const [channelID, setChannelID] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [socket, setSocket] = useState(null);
  const [openConversationLimitPopup, setOpenConversationLimitPopup] =
    useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [jobsList, setJobList] = useState([]);
  const [assignedCount, setAssignedCount] = useState(0);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedChatJobs, setSelectedChatJobs] = useState([]);
  const [showMatchPercentagePopup, setShowMatchPercentagePopup] =
    useState(false);
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const debounceTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pingTimeoutRef = useRef(null);
  const responseReceivedRef = useRef(false);
  const isConnectingRef = useRef(false);
  const lastMessageRef = useRef(null);
  const messageQueueRef = useRef([]);
  const lastPingTimeRef = useRef(null);

  const isAwaitingReply =
    searchMessages.some((m) => m.isLoading) ||
    recommendedMessages.some((m) => m.isLoading);
  const resolveLoadingMessages = (text) => {
    const apply = (setFn) =>
      setFn((prev) => {
        const idx = prev.findIndex((m) => m.isLoading || m.isStreaming);
        if (idx === -1) return prev;
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          text,
          isLoading: false,
          isStreaming: false,
        };
        return updated;
      });
    apply(setSearchMessages);
    apply(setRecommendedMessages);
    responseReceivedRef.current = true;
    setIsBotProcessing(false);
  };
  useThinkingFallback({
    active: isAwaitingReply,
    getChannelId: () => channelID || channelIdCache.get(candidateId),
    responseReceivedRef,
    onRecovered: (answer) => resolveLoadingMessages(answer),
    onExhausted: () => resolveLoadingMessages(RECOVERY_FAILED_MESSAGE),
  });

  const MAX_RECONNECT_ATTEMPTS = 5;
  const BASE_RECONNECT_DELAY = 1000;
  const PING_INTERVAL = 30000;
  const PING_TIMEOUT = 10000;

  const jobIcons = [
    androidIcon,
    pythonIcon,
    reactIcon,
    phpIcon,
    excelIcon,
    javaIcon,
  ];
  // Drop "unlikely" fitness tiers: poor matches aren't surfaced as
  // recommendations. Untiered items (rerank fallback/timeout) are kept so the
  // tab degrades gracefully to threshold labels instead of going empty.
  const recommendedJobs = Array.isArray(candidate?.recommendedJobs)
    ? (candidate?.recommendedJobs).filter(
        (job) => String(job?.fitness || "").toLowerCase() !== "unlikely",
      )
    : [];
  const isRecommendedJobsReady = Array.isArray(candidate?.recommendedJobs);

  const fetchJobsList = useCallback(
    async (jobs = [], botMessageId, isSearch = false) => {
      const detailedJobs = await Promise.all(
        jobs.map(async (job) => {
          try {
            const res = await get(
              VYLABS_BASE_URL,
              `${GET_JOB_DETAILS_BY_ID}?id=${job.id}`,
            );
            return res.job || null;
          } catch (err) {
            console.error("Error fetching job by ID:", err);
            return null;
          }
        }),
      );

      const filteredJobs = detailedJobs.filter(Boolean);
      setJobList(filteredJobs);
      const updateState = (setMessagesFn) => {
        setMessagesFn((prev) => {
          const updated = [...prev];
          const botMessageIndex = updated.findIndex(
            (msg) => msg.id === botMessageId && msg.sender === "bot",
          );

          if (botMessageIndex !== -1) {
            updated[botMessageIndex] = {
              ...updated[botMessageIndex],
              jobs: filteredJobs,
            };
          }
          return updated;
        });
      };

      if (isSearch) {
        updateState(setSearchMessages);
      } else {
        updateState(setRecommendedMessages);
      }

      setIsBotProcessing(false);
    },
    [],
  );

  const handleToggleSelectMode = () => {
    setIsSelectMode((prev) => {
      if (prev) {
        setSelectedChatJobs([]);
      }
      return !prev;
    });
  };

  const handleJobSelect = (job, event) => {
    event && event.stopPropagation();

    setSelectedChatJobs((prev) => {
      if (prev.some((j) => j.id === job.id)) {
        return prev.filter((j) => j.id !== job.id);
      } else {
        if (prev.length >= 10) {
          Toast("error", "You can select only up to 10 jobs.");
          return prev;
        }
        return [...prev, job];
      }
    });
  };

  const handleReconnect = useCallback(() => {
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      setIsBotProcessing(false);
      return;
    }
    const delay = Math.min(
      BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts + 1) +
        Math.random() * 1000,
      60000,
    );

    reconnectTimeoutRef.current = setTimeout(() => {
      setReconnectAttempts((prev) => prev + 1);
      initializeWebSocket();
    }, delay);
  }, [reconnectAttempts]);

  const initializeWebSocket = useCallback(() => {
    if (isConnectingRef.current) {
      console.log("Already attempting to connect, skipping...");
      return;
    }

    isConnectingRef.current = true;
    if (socketRef.current) {
      socketRef.current.close();
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (pingTimeoutRef.current) {
      clearTimeout(pingTimeoutRef.current);
      pingTimeoutRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    responseReceivedRef.current = false;

    const token = localStorage.getItem("token");
    const wsProtocol =
      window.location.protocol === "https:" ? "wss://" : "wss://";
    const wsUrl = `${wsProtocol}${WEB_SOCKET_URL}/ws?token=${encodeURIComponent(
      "Bearer " + token,
    )}`;

    try {
      console.log("Creating new WebSocket connection...");
      const ws = new WebSocket(wsUrl);
      socketRef.current = ws;
      setSocket(ws);

      ws.onopen = () => {
        console.log("WebSocket connection established");
        setReconnectAttempts(0);
        isConnectingRef.current = false;
        if (lastMessageRef.current) {
          console.log("Resending last message:", lastMessageRef.current);
          ws.send(lastMessageRef.current);
        }
        if (messageQueueRef.current.length > 0) {
          console.log(
            `Processing ${messageQueueRef.current.length} queued messages`,
          );
          messageQueueRef.current.forEach((msg) => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(msg);
            }
          });
          messageQueueRef.current = [];
        }
        startPingInterval(ws);
      };

      ws.onmessage = (event) => {
        if (event.data === "pong") {
          if (pingTimeoutRef.current) clearTimeout(pingTimeoutRef.current);
          pingTimeoutRef.current = null;
          return;
        }

        try {
          console.log("WebSocket Message Received:", event.data);
          const [topic, payload] = event.data.split("|");
          const trimmedTopic = topic.split(":")[0].trim();

          if (trimmedTopic === "screening-message-delta") {
            if (!STREAMING_ENABLED) return;
            const sepIdx = event.data.indexOf("|");
            const rawPayload =
              sepIdx !== -1 ? event.data.substring(sepIdx + 1) : "";
            let chunk = "";
            try {
              chunk = JSON.parse(rawPayload);
            } catch (e) {
              chunk = rawPayload;
            }
            if (typeof chunk !== "string") {
              chunk = chunk == null ? "" : String(chunk);
            }
            if (chunk) {
              const appendChunk = (setMessagesFn) => {
                setMessagesFn((prev) => {
                  const idx = prev.findIndex(
                    (m) => m.isLoading || m.isStreaming,
                  );
                  if (idx === -1) return prev;
                  const updated = [...prev];
                  updated[idx] = {
                    ...updated[idx],
                    text: (updated[idx].text || "") + chunk,
                    isLoading: false,
                    isStreaming: true,
                  };
                  return updated;
                });
              };
              appendChunk(setSearchMessages);
              appendChunk(setRecommendedMessages);
            }
            return;
          }

          const parseNestedJSON = (str) => {
            try {
              if (str === "reading message" || str === '"reading message"') {
                return null;
              }
              let cleaned = str
                .replace(/^"+|"+$/g, "")
                .replace(/\\"/g, '"')
                .replace(/\\\\/g, "\\");
              let parsed = JSON.parse(cleaned);
              if (parsed?.answer && typeof parsed.answer === "string") {
                const ans = parsed.answer.trim();

                if (ans.startsWith("{") && ans.endsWith("}")) {
                  try {
                    const inner = JSON.parse(ans);
                    if (inner.answer) parsed.answer = inner.answer;
                  } catch {}
                }
              }

              return parsed;
            } catch (error) {
              console.error("Parse failed:", error, { raw: str });
              return null;
            }
          };

          const response = parseNestedJSON(payload);
          console.log("Parsed response:", response);

          if (!response || !response.answer) {
            console.error("Invalid response format");
            setIsBotProcessing(false);
            return;
          }
          const extractJobs = (text) => {
            const jobs = [];
            const lines = text.split("\n");
            const mainJobRegex =
              /\d+\.\s+\*\*(.*?)\*\*(?: - Location: (.*?))?$/;
            const idSectionRegex = /-\s*(.*?)\s*\(ID:\s*([a-f0-9]+)\)/;
            lines.forEach((line) => {
              const mainMatch = line.match(mainJobRegex);
              if (mainMatch) {
                jobs.push({
                  title: mainMatch[1].trim(),
                  location: mainMatch[2]?.trim(),
                });
              }
            });
            lines.forEach((line) => {
              const idMatch = line.match(idSectionRegex);
              if (idMatch) {
                const jobTitle = idMatch[1].trim();
                const jobId = idMatch[2].trim();
                const jobIndex = jobs.findIndex((j) => j.title === jobTitle);
                if (jobIndex !== -1) {
                  jobs[jobIndex].id = jobId;
                } else {
                  jobs.push({
                    title: jobTitle,
                    id: jobId,
                  });
                }
              }
            });
            return jobs;
          };

          const extractedJobs = extractJobs(response.answer);
          console.log("Extracted jobs:", extractedJobs);
          const updateMessages = (setMessagesFn, isSearch = false) => {
            setMessagesFn((prev) => {
              const updated = [...prev];
              const loadingIndex = updated.findIndex(
                (msg) => msg.isLoading || msg.isStreaming,
              );

              if (loadingIndex !== -1) {
                updated[loadingIndex] = {
                  ...updated[loadingIndex],
                  text: sanitizeBotText(response.answer),
                  isLoading: false,
                  isStreaming: false,
                  jobs: extractedJobs,
                  isFirstResponse: isFirstBotResponse && isSearch,
                };
                const botMessageId = updated[loadingIndex].id;
                if (extractedJobs.length > 0 && botMessageId) {
                  fetchJobsList(extractedJobs, botMessageId, isSearch);
                } else {
                  setIsBotProcessing(false);
                }
              } else {
                setIsBotProcessing(false);
              }

              return updated;
            });
          };

          if (trimmedTopic === "screening-send-message-completed") {
            updateMessages(setSearchMessages, true);
          } else if (trimmedTopic === "screening-message-update") {
            updateMessages(setRecommendedMessages, false);
          } else if (trimmedTopic === "screening-total-tokens-err") {
            setOpenConversationLimitPopup(true);
            setIsBotProcessing(false);
          }
        } catch (error) {
          console.error("WebSocket message processing error:", error);
          setIsBotProcessing(false);
        }
      };

      ws.onclose = (event) => {
        console.log("WebSocket connection closed", event);
        isConnectingRef.current = false;
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        if (pingTimeoutRef.current) {
          clearTimeout(pingTimeoutRef.current);
          pingTimeoutRef.current = null;
        }
        console.log(
          `Connection closed with code ${event.code}, attempting to reconnect...`,
        );
        handleReconnect();
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        isConnectingRef.current = false;
        handleReconnect();
      };
    } catch (error) {
      console.error("Error creating WebSocket:", error);
      isConnectingRef.current = isConnectingRef.current = false;
      handleReconnect();
    }
  }, [handleReconnect, fetchJobsList, isFirstBotResponse]);

  const startPingInterval = useCallback((ws) => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (ws.readyState === WebSocket.OPEN) {
      console.log("Sending initial ping");
      ws.send("ping");
      lastPingTimeRef.current = Date.now();
      pingTimeoutRef.current = setTimeout(() => {
        const timeSinceLastPing = Date.now() - lastPingTimeRef.current;
        if (timeSinceLastPing > PING_TIMEOUT) {
          console.log("No pong received within timeout, reconnecting...");
          ws.close(1000, "Ping timeout");
        }
      }, PING_TIMEOUT);
    }
    pingIntervalRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        console.log("Sending ping");
        ws.send("ping");
        lastPingTimeRef.current = Date.now();
        if (pingTimeoutRef.current) {
          clearTimeout(pingTimeoutRef.current);
        }
        pingTimeoutRef.current = setTimeout(() => {
          const timeSinceLastPing = Date.now() - lastPingTimeRef.current;
          if (timeSinceLastPing > PING_TIMEOUT) {
            console.log("No pong received within timeout, reconnecting...");
            ws.close(1000, "Ping timeout");
          }
        }, PING_TIMEOUT);
      }
    }, PING_INTERVAL);
  }, []);

  const handleSearch = useCallback(async () => {
    if (!searchQuery?.trim()) {
      Toast("error", "Please enter a search query.");
      return;
    }

    if (selectedChatJobs.length === 0) {
      Toast("error", "You have to select at least 1 job.");
      return;
    }

    setIsBotProcessing(true);
    const messageId = uuidv4();
    const botMessageId = uuidv4();

    let contextJobs = recommendedJobs;
    if (selectedChatJobs.length > 0) {
      contextJobs = recommendedJobs.filter((job) =>
        selectedChatJobs.some((selected) => selected.id === job.id),
      );
    }

    if (!hasSearched) {
      setHasSearched(true);
      setSearchMessages([
        {
          id: uuidv4(),
          sender: "user",
          text: `recommend jobs for ${candidate.name}`,
        },
        {
          id: uuidv4(),
          sender: "bot",
          text: "",
          jobs: contextJobs,
          isLoading: false,
          isFirstResponse: true,
        },
        {
          id: messageId,
          sender: "user",
          text: searchQuery,
        },
        {
          id: botMessageId,
          sender: "bot",
          text: "",
          isLoading: true,
          jobs: [],
        },
      ]);
    } else {
      setSearchMessages((prev) => [
        ...prev,
        {
          id: messageId,
          sender: "user",
          text: searchQuery,
        },
        {
          id: botMessageId,
          sender: "bot",
          text: "",
          isLoading: true,
          jobs: [],
        },
      ]);
    }

    try {
      let currentChannelID = channelIdCache.get(candidateId) || channelID;
      if (!currentChannelID) {
        const createChatChannelResponse = await post(
          VYLABS_BASE_URL,
          CREATE_CHAT_CHANNEL_URL,
          { chat_channel_type: "job" },
        );
        currentChannelID = createChatChannelResponse.ID;
        setChannelID(currentChannelID);
        channelIdCache.set(candidateId, currentChannelID);
        setIsFirstMessage(true);
        try {
          await post(VYLABS_BASE_URL, GET_CURRENT_CONVERSATION_TITLE, {
            chat_channel_id: currentChannelID,
            message: searchQuery,
          });
        } catch (error) {
          console.error(
            "Error triggering GET_CURRENT_CONVERSATION_TITLE:",
            error,
          );
        }
      }

      const formData = new FormData();
      formData.append("channel_id", currentChannelID);
      formData.append("message_content", searchQuery);
      if (isFirstMessage) {
        formData.append("candidate_id", candidateId);
        const jobIds = selectedChatJobs.map((job) => job.id);
        formData.append("job_id", JSON.stringify(jobIds));
        setIsFirstMessage(false);
      }
      await post(VYLABS_BASE_URL, PROCESS_JOB_CONVERSATION, formData);
      initializeWebSocket();
    } catch (error) {
      console.error("Error in job search:", error);
      if (
        error.response?.data &&
        error.response.data.includes("chat channel is blocked")
      ) {
        setSearchMessages((prev) =>
          prev.filter((msg) => msg.id !== messageId && msg.id !== botMessageId),
        );
        setOpenConversationLimitPopup(true);
        setIsBotProcessing(false);
      } else {
        setSearchMessages((prev) => {
          const updated = [...prev];
          const loadingIndex = updated.findIndex((msg) => msg.isLoading);
          if (loadingIndex !== -1) {
            updated[loadingIndex] = {
              ...updated[loadingIndex],
              text: "Error processing request. Please try again.",
              isLoading: false,
              jobs: [],
            };
          }
          return updated;
        });
        setIsBotProcessing(false);
      }
    } finally {
      setSearchQuery("");
    }
  }, [
    searchQuery,
    channelID,
    initializeWebSocket,
    candidateId,
    recommendedMessages,
    candidate.name,
    recommendedJobs,
    isFirstMessage,
    selectedChatJobs,
    hasSearched,
  ]);

  const handleBackToList = () => {
    setHasSearched(false);
    setSearchMessages([]);
    setSearchQuery("");
    setIsFirstBotResponse(true);
    setIsFirstMessage(true);
    setChannelID(null);
    channelIdCache.delete(candidateId);
    setIsSelectMode(false);
    setSelectedChatJobs([]);
    setIsFromCandidateRecommendedJobs(false);
    if (socketRef.current) {
      socketRef.current.close();
    }
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleOpenJobsSideContainer = (job, index, tab) => {
    setIsFromCandidateRecommendedJobs(true);
    setSelectedJob({ ...job });
    setActiveTab(tab);
    setJobsSideContainerOpen(true);
  };

  const handleCloseJobsSideContainer = () => {
    setIsFromCandidateRecommendedJobs(false);
    setJobsSideContainerOpen(false);
    setSelectedJob(null);
    setActiveTab("Job Details");
    setAssignedCandidates([]);
  };

  const handleClearSearch = () => {
    setSearchMessages([]);
    setHasSearched(false);
    setSearchQuery("");
    setIsFirstBotResponse(true);
    setIsFirstMessage(true);
    if (socketRef.current) {
      socketRef.current.close();
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (socketRef.current) {
        socketRef.current.close();
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (pingTimeoutRef.current) {
        clearTimeout(pingTimeoutRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      setIsFromCandidateRecommendedJobs(false);
    };
  }, [setIsFromCandidateRecommendedJobs, CandidatehasSearched]);

  useEffect(() => {
    setIsFromCandidateRecommendedJobs(false);
  }, [candidate, setIsFromCandidateRecommendedJobs, CandidatehasSearched]);

  useEffect(() => {
    scrollToBottom();
  }, [searchMessages, recommendedMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const renderBotResponse = (message) => {
    if (message.sender !== "bot") return null;
    const { summary } = parseMessageText(message.text);

    return (
      <div className="bot-message-container">
        <img src={BotReplyIcon} alt="BotReplyIcon" className="replay-icon" />
        <div
          className="bot-message-content"
          style={
            message.isLoading
              ? { border: "none" }
              : {
                  border: "1px solid var(--border__color)",
                  padding: "15px",
                  borderRadius: "8px",
                }
          }
        >
          {message.isLoading ? (
            <ThinkingBubble />
          ) : (
            <div className="text-with-card-container">
              <div className="summary-container">
                {summary &&
                  summary.split("\n").map((line, idx) => {
                    const trimmedLine = line.trim();
                    const bulletContent = getBulletContent(trimmedLine);
                    if (bulletContent !== null) {
                      return (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            marginBottom: "5px",
                          }}
                        >
                          <img
                            src={BotResponseStarIcon}
                            alt="bullet"
                            style={{
                              width: "18px",
                              height: "18px",
                              marginRight: "8px",
                              marginTop: "10px",
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              fontSize: "18px",
                              textAlign: "left",
                              color: "var(--chat-text)",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {renderInlineMarkdown(bulletContent)}
                          </span>
                        </div>
                      );
                    }
                    return (
                      <p
                        key={idx}
                        style={{
                          fontSize: "18px",
                          textAlign: "left",
                          color: "var(--chat-text)",
                          whiteSpace: "pre-wrap",
                          marginBottom: "5px",
                          marginTop: "0px",
                          minHeight: line.trim() ? "auto" : "1em",
                        }}
                      >
                        {renderBotContent(line)}
                      </p>
                    );
                  })}
                {message.isStreaming && (
                  <>
                    <style>{`@keyframes vyblink{0%,100%{opacity:1}50%{opacity:0}}`}</style>
                    <span
                      style={{
                        display: "inline-block",
                        width: "7px",
                        height: "16px",
                        marginLeft: "2px",
                        background: "var(--chat-text)",
                        animation: "vyblink 1s steps(1) infinite",
                        verticalAlign: "text-bottom",
                      }}
                    />
                  </>
                )}
              </div>
              {message.jobs && message.jobs.length > 0 && (
                <div className="jobs-grid">
                  {message.jobs.map((job, index) => (
                    <JobsCard
                      key={index}
                      jobId={job.id}
                      title={job.title}
                      subTitle={job.job_role || job.title}
                      role={job.job_role || job.title}
                      experience={job.years_experience}
                      jobtype={job.job_type}
                      actionText={true}
                      skills={job.skills ? job.skills.split(", ") : []}
                      minSalary={job.salary_min}
                      maxSalary={job.salary_max}
                      client={job?.client?.name}
                      jobRole={job?.job_role}
                      tags={job.tags || []}
                      location={job.location}
                      client_contact_person={job?.client?.contact_person}
                      cardbackgroundColor="linear-gradient(rgb(66 185 120 / 31%), #D6FFE8)"
                      cardColor="rgb(8 104 29)"
                      skillbackgroundColor="rgb(12 122 51 / 74%)"
                      skillColor="#fff"
                      isActive={job.active}
                      icon={jobIcons[index % jobIcons.length]}
                      showEditIcon={false}
                      matchPercentage={job.match_percentage}
                      fitness={job.fitness}
                      viewJobEventHandler={(e) => {
                        e.stopPropagation();
                        handleOpenJobsSideContainer(job, index, "Job Details");
                      }}
                      assignCandidateEventHandler={(e) => {
                        e.stopPropagation();
                        handleOpenJobsSideContainer(
                          job,
                          index,
                          "Assigned Candidates",
                        );
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderChatMessages = () => {
    return (
      <div className="chat-messages-container recommended-jobs-no-inner-scroll">
        {searchMessages.map((message, index) => (
          <div key={index} className={`message-wrapper ${message.sender}`}>
            {message.sender === "user" ? (
              <div className="user-message-container">
                <div className="user-message-content">
                  <p className="user-message-query-text">{message.text}</p>
                </div>
              </div>
            ) : (
              renderBotResponse(message)
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
    );
  };

  const hasRecommendedJobs = recommendedJobs.length > 0;
  const showRecommendedJobsLoader =
    isRecommendedJobsLoading || !isRecommendedJobsReady;
  return (
    <div className="recommended-jobs-container">
      <JobStatusUpdatePopup />
      <div className="recommended-jobs-list">
        {hasSearched ? (
          <div className="search-results-container">
            {renderChatMessages()}
            <div className="search-box-bottom" style={{ padding: "15px" }}>
              <RecommendedSearchBox
                isDarkMode={isDarkMode}
                recommendedSearchBoxTitle="VybogTAL Chat"
                askMeAnythingPlaceholder="Search for 'Frontend Developer Job at Texas, USA'"
                buttonLabel="Chat"
                TextAreaStyle={{ height: "55px" }}
                inputsectionstylewrap={{ paddingBottom: "20px" }}
                inputsectionstyle={{ paddingTop: "1px" }}
                askMeAnythingValue={searchQuery}
                onAskChange={handleInputChange}
                onSearch={handleSearch}
                onClear={handleClearSearch}
                showBadge={true}
                askmeanythingOnKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                isSearchLoading={isBotProcessing}
                showJobDescriptionField={false}
                showAttachResume={false}
                submitErrorMessage="Please enter a search query."
              />
            </div>
          </div>
        ) : (
          <div className="search-results-container">
            {showRecommendedJobsLoader || candidate?.isRankingJobs ? (
              <div className="loader-center-container">
                <div
                  className="cephea-loading-state"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <VybogTALLoaderAnimation />
                  {candidate?.isRankingJobs && (
                    <p
                      style={{
                        marginTop: "8px",
                        color: "var(--header__color)",
                      }}
                    >
                      Finding your best matches…
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="chat-messages-container recommended-jobs-no-inner-scroll">
                {hasRecommendedJobs ? (
                  <div className="reccomend-jobs-search-results-container">
                    <div className="bot-response-container">
                      <div
                        className="job-bot-response"
                        style={{ width: "100%" }}
                      >
                        <div className="bot-message">
                          <div className="reply-message">
                            <div className="text-with-card-container">
                              <div
                                className="recommendation-results-header"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  marginBottom: "10px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: "bold",
                                      fontSize: "16px",
                                      color: "var(--header__color)",
                                    }}
                                  >
                                    Recommended Jobs
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      color: "#6B7280",
                                      marginTop: "5px",
                                    }}
                                  >
                                    Select a job to ask VybogTAL about fit,
                                    required skills, or company details
                                  </span>
                                </div>

                                <div className="recommendation-action-bar">
                                  {!isSelectMode && (
                                    <Button
                                      label="How is fit calculated?"
                                      event={() =>
                                        setShowMatchPercentagePopup(true)
                                      }
                                      iconSrc={networkIcon}
                                      iconWidth="18px"
                                      className="recommend-action-button"
                                    />
                                  )}
                                  {!isSelectMode ? (
                                    <CustomTooltip
                                      title={
                                        "You can select up to 10 jobs to chat"
                                      }
                                      arrow
                                    >
                                      <Button
                                        label="Select to Chat"
                                        event={handleToggleSelectMode}
                                        iconSrc={VybogTALWhiteIcon}
                                        iconWidth="16px"
                                        className="recommend-action-button"
                                      />
                                    </CustomTooltip>
                                  ) : (
                                    <div className="recommend-selection-actions">
                                      {selectedChatJobs.length > 0 && (
                                        <span className="recommend-selected-jobs-count">
                                          {selectedChatJobs.length === 1
                                            ? `Selected Job (${selectedChatJobs.length})`
                                            : `Selected Jobs (${selectedChatJobs.length})`}
                                          <Badge
                                            badgeContent="BETA"
                                            sx={{
                                              "& .MuiBadge-badge": {
                                                fontSize: "10px",
                                                height: "20px",
                                                marginTop: "-2px",
                                                marginLeft: "5px",
                                                minWidth: "32px",
                                                padding: "2px 8px",
                                                backgroundColor: "#fff4e5",
                                                color: "#ffa408",
                                                border: "1px solid #ffa408",
                                                position: "static",
                                                transform: "none",
                                                borderRadius: "12px",
                                              },
                                            }}
                                          />
                                        </span>
                                      )}
                                      <Button
                                        label="Cancel"
                                        event={handleToggleSelectMode}
                                        isCancel={true}
                                        className="recommend-action-button"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="recommended-jobs-grid">
                                {recommendedJobs.map((job, index) => {
                                  const isSelected =
                                    isSelectMode &&
                                    selectedChatJobs.some(
                                      (j) => j.id === job.id,
                                    );
                                  return (
                                    <div
                                      className={`job-card-wrapper ${
                                        isSelected ? "selected" : ""
                                      }`}
                                      key={index + "_outer"}
                                      onClick={(e) => {
                                        if (isSelectMode) {
                                          handleJobSelect(job, e);
                                        }
                                      }}
                                      style={{
                                        cursor: isSelectMode
                                          ? "pointer"
                                          : "default",
                                      }}
                                    >
                                      <JobsCard
                                        key={index}
                                        jobId={job.id}
                                        title={job.title}
                                        subTitle={job.job_role || job.title}
                                        role={job.job_role || job.title}
                                        experience={job.years_experience}
                                        jobtype={job.job_type}
                                        actionText={true}
                                        skills={
                                          job.skills
                                            ? job.skills.split(", ")
                                            : []
                                        }
                                        minSalary={job.salary_min}
                                        maxSalary={job.salary_max}
                                        client={job?.client?.name}
                                        jobRole={job?.job_role}
                                        tags={job.tags || []}
                                        location={job.location}
                                        client_contact_person={
                                          job?.client_contact
                                            ? job?.client_contact?.name
                                            : job?.client?.contact_person
                                        }
                                        cardbackgroundColor="linear-gradient(rgb(66 185 120 / 31%), #D6FFE8)"
                                        cardColor="rgb(8 104 29)"
                                        skillbackgroundColor="rgb(12 122 51 / 74%)"
                                        skillColor="#fff"
                                        isActive={job.active}
                                        icon={jobIcons[index % jobIcons.length]}
                                        showEditIcon={false}
                                        viewJobEventHandler={(e) => {
                                          e.stopPropagation();
                                          handleOpenJobsSideContainer(
                                            job,
                                            index,
                                            "Job Details",
                                          );
                                        }}
                                        assignCandidateEventHandler={(e) => {
                                          e.stopPropagation();
                                          handleOpenJobsSideContainer(
                                            job,
                                            index,
                                            "Assigned Candidates",
                                          );
                                        }}
                                        matchPercentage={job.match_percentage}
                                        fitness={job.fitness}
                                        isSelectMode={isSelectMode}
                                        isSelected={isSelected}
                                        handleCardSelect={() =>
                                          handleJobSelect(job)
                                        }
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <>
                      <h4
                        style={{
                          textAlign: "center",
                          marginBottom: "-20px",
                        }}
                      >
                        There are no recommended jobs for this candidate!
                      </h4>
                      <AnimationPlayer
                        animationSrc={NojobsAnimation}
                        playerHeight="300px"
                        playerWidth="300px"
                      />
                    </>
                  </div>
                )}
              </div>
            )}
            {hasRecommendedJobs &&
              !CandidatehasSearched &&
              !isBotProcessing &&
              !hideSearchBox &&
              selectedChatJobs.length > 0 && (
                <div className="search-box-bottom" style={{ padding: "15px" }}>
                  <RecommendedSearchBox
                    isDarkMode={isDarkMode}
                    recommendedSearchBoxTitle="VybogTAL Chat"
                    askMeAnythingPlaceholder="Search for 'Frontend Developer Job at Texas, USA'"
                    buttonLabel="Chat"
                    TextAreaStyle={{ height: "55px" }}
                    inputsectionstylewrap={{ paddingBottom: "20px" }}
                    inputsectionstyle={{ paddingTop: "1px" }}
                    askMeAnythingValue={searchQuery}
                    onAskChange={handleInputChange}
                    onSearch={handleSearch}
                    showBadge={true}
                    askmeanythingOnKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSearch();
                      }
                    }}
                    showJobDescriptionField={false}
                    showAttachResume={false}
                  />
                </div>
              )}
          </div>
        )}
      </div>
      <JobsDetailsContainer
        isOpen={jobsSideContainerOpen}
        onClose={handleCloseJobsSideContainer}
        selectedJob={selectedJob}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isEditable={false}
        hasSearched={hasSearched}
        hasRecommendedJobs={hasRecommendedJobs}
        assignedCount={assignedCount}
        setAssignedCount={setAssignedCount}
        onAssignedCountChange={setAssignedCount}
        showRecommendedCandidatesTab={false}
        jobId={selectedJob?.id}
      />
      <ConfirmationPopup
        isOpen={openConversationLimitPopup}
        onConfirm={() => {
          handleBackToList();
          setOpenConversationLimitPopup(false);
        }}
        title="Conversation limit reached."
        message="You have reached the limit of this conversation. Please start a new one"
        confirmLabel="New Chat"
        cancelLabel=""
      />
      {showMatchPercentagePopup && (
        <MatchPercentage
          isRerank
          onClose={() => setShowMatchPercentagePopup(false)}
        />
      )}
    </div>
  );
}

export default RecommendedJobs;
